<template>
  <div
    data-qa="preview_menu"
    class="preview-menu"
    :class="{
      'preview-menu--mobile': isMobile && isMobileInitial,
      'preview-menu--desktop-initial': isMobile && !isMobileInitial,
    }"
  >
    <div
      class="preview-menu__bar bg-gray-0"
      :style="{ height: previewMenuOffset }"
    >
      <div
        v-if="isMobileInitial"
        class="d-flex align-center justify-center py-2"
      >
        <v-btn
          data-qa="preview_menu_share"
          text
          height="40"
          class="text-none px-4"
          @click="handleShare"
        >
          <span class="ui-icon-link-2 ui-icon-fz-big icon-secondary--text mr-3" />
          <span class="text-body-2 text--text">
            {{ tStatic('Поделиться ссылкой') }}
          </span>
        </v-btn>
      </div>

      <div
        v-else
        class="d-flex align-center py-4"
        :class="context.request.feature_flags.ff_lpu_branding_page_v4
          ? 'container'
          : 'b-container'"
      >
        <div class="d-flex align-center flex-grow-1 w-0">
          <v-btn
            data-qa="preview_menu_edit"
            text
            height="40"
            class="text-none px-4"
            :href="editUrl"
          >
            <span class="ui-icon-create ui-icon-fz-big icon-secondary--text mr-3" />
            <span class="text-body-2 text--text">
              {{ tStatic('Режим редактирования') }}
            </span>
          </v-btn>
        </div>

        <div
          data-qa="preview_menu_devices"
          class="d-flex align-center gc-1 flex-shrink-0"
        >
          <v-btn
            data-qa="preview_menu_desktop"
            icon
            height="40"
            width="40"
            :class="!isMobile ? 'primary--text' : 'icon-secondary--text'"
            :aria-pressed="!isMobile"
            :aria-label="tStatic('Настольная версия')"
            @click="changeFlavour('full')"
          >
            <span class="ui-icon-monitor ui-icon-fz-big" />
          </v-btn>
          <v-btn
            data-qa="preview_menu_mobile"
            icon
            height="40"
            width="40"
            :class="isMobile ? 'primary--text' : 'icon-secondary--text'"
            :aria-pressed="isMobile"
            :aria-label="tStatic('Мобильная версия')"
            @click="changeFlavour('mobile')"
          >
            <span class="ui-icon-phone-check ui-icon-fz-big" />
          </v-btn>
        </div>

        <div class="d-flex align-center justify-end flex-grow-1 gc-2 w-0">
          <v-btn
            data-qa="preview_menu_share"
            height="40"
            class="text-none px-4"
            text
            @click="handleShare"
          >
            <span class="ui-icon-link-2 ui-icon-fz-big icon-secondary--text mr-3" />
            <span class="text-body-2 text--text">
              {{ tStatic('Поделиться ссылкой') }}
            </span>
          </v-btn>

          <v-btn
            data-qa="preview_menu_moderation"
            color="primary"
            class="text-none px-4"
            height="40"
            :href="moderationUrl"
            depressed
          >
            {{ tStatic('Модерация') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  onBeforeUnmount,
  watch,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import copy from 'copy-to-clipboard'
import useSnackbar from 'src/composables/useSnackbar'
import useContextStore from 'src/store/useContextStore'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import useFlavour from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/PreviewMenu/composables/useFlavour'
import {
  PREVIEW_MENU_OFFSET_DESKTOP,
  PREVIEW_MENU_OFFSET_MOBILE,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/PreviewMenu/constants/previewMenuOffset'

const props = defineProps<{
  lpuId: number
}>()

const isMobile = useIsMobile()
const { context } = useContextStore()

const { isMobileInitial, changeFlavour } = useFlavour(isMobile)
const previewMenuOffset = computed(() => (
  isMobileInitial.value ? PREVIEW_MENU_OFFSET_MOBILE : PREVIEW_MENU_OFFSET_DESKTOP
))

const editUrl = computed(() => `/cabinet/branding/?lpu=${props.lpuId}`)
const moderationUrl = computed(() => `/cabinet/branding/moderation/?lpu=${props.lpuId}`)

const copyLinkText = tStatic('Ссылка скопирована')
const snackbar = useSnackbar()

function handleShare() {
  copy(`${window.location.origin}${window.location.pathname}`)
  snackbar.show(copyLinkText)
}

if (typeof document !== 'undefined') {
  document.body.classList.add('branding-lpu-page-preview')

  if (isMobile.value && !isMobileInitial.value) {
    document.body.classList.add('branding-lpu-page-preview--mobile')
  }
}

watch(previewMenuOffset, offset => {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.style.setProperty('--preview-menu-offset', offset)
}, { immediate: true })

onBeforeUnmount(() => {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.style.removeProperty('--preview-menu-offset')
})
</script>

<style lang="scss" scoped>
.preview-menu {
  // Вне flex/gap родителя (.lpu-branding) — не сдвигает обычную вёрстку
  position: absolute;
  width: 0;
  height: 0;
  overflow: visible;

  &__bar {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    z-index: 110;

    // shadow new из макета
    box-shadow:
      0 1px 2px rgba(5, 23, 40, 0.08),
      0 0 2px rgba(21, 40, 92, 0.06),
      0 2px 24px rgba(9, 42, 72, 0.08);
  }
}
</style>

<style lang="scss">
/*
 * Offset на html (:has), чтобы SSR/первый кадр совпадали с гидратацией и
 * getComputedStyle(document.documentElement) / JS setProperty читали одно место.
 * Значения — как PREVIEW_MENU_OFFSET_* в previewMenuOffset.ts.
 */
@supports selector(:has(*)) {
  html:has(.preview-menu--mobile) {
    --preview-menu-offset: 56px;
  }

  html:has(.preview-menu:not(.preview-menu--mobile)) {
    --preview-menu-offset: 72px;
  }

  body:has(.preview-menu--desktop-initial) {
    --body-preview-max-width: 375px;
    --body-preview-margin: 0 auto;

    max-width: var(--body-preview-max-width);
    margin: var(--body-preview-margin);

    .the-header__sticky-line.sticky {
      max-width: var(--body-preview-max-width);
      margin: var(--body-preview-margin);
    }

    .section-menu.section-menu--pinned {
      max-width: var(--body-preview-max-width);
      margin: var(--body-preview-margin);
    }
  }
}

body:has(.preview-menu) {
  padding-top: var(--preview-menu-offset, 0);
}

body.branding-lpu-page-preview--mobile {
  --body-preview-max-width: 375px;
  --body-preview-margin: 0 auto;

  max-width: var(--body-preview-max-width);
  margin: var(--body-preview-margin);

  .the-header__sticky-line.sticky {
    max-width: var(--body-preview-max-width);
    margin: var(--body-preview-margin);
  }

  .section-menu.section-menu--pinned {
    max-width: var(--body-preview-max-width);
    margin: var(--body-preview-margin);
  }
}

body.branding-lpu-page-preview,
body:has(.preview-menu) {
  .the-header__sticky-line.sticky .container {
    width: calc(100% - 16px);
  }

  .the-header__sticky-line.sticky .container .container {
    width: initial;
  }

  .the-header__sticky-line.sticky {
    top: var(--preview-menu-offset, 0) !important;
  }
}
</style>
